/**
 * useMomentVideo — Jun 2026 (Option A lip-sync moments)
 * Polls /api/ava-video/moment-status until the lip-synced clip for a
 * (session, momentType) pair is ready, then returns its playable URL.
 * `dismiss()` hides it permanently for this session (after playback/skip).
 *
 * Iter47 fixes: interval id is LOCAL to each effect run (a stale run can no
 * longer kill the new session's poll), the catch path respects `cancelled`,
 * and transient errors (429 / network) back off and retry instead of
 * stopping forever.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../api/client';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_ATTEMPTS = 40;        // ~3.5 minutes at 5s intervals
const MAX_CONSECUTIVE_ERRORS = 5;

export const useMomentVideo = (sessionId, momentType, enabled = true, resetKey = 0, avaId = null) => {
  const [videoUrl, setVideoUrl] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setVideoUrl(null);
    setDismissed(false);
  }, [sessionId, momentType, resetKey]);

  useEffect(() => {
    if (!sessionId || !momentType || !enabled || dismissed) return undefined;
    let cancelled = false;
    let intervalId = null;
    let attempts = 0;
    let notFoundCount = 0;
    let errorCount = 0;

    const stop = () => { if (intervalId) clearInterval(intervalId); };

    const check = async () => {
      attempts += 1;
      try {
        // ava_id lets the backend face-guard reject any clip whose face
        // doesn't match the AvA the user is looking at (Neo wrong-face bug).
        const q = avaId ? `?ava_id=${encodeURIComponent(avaId)}` : '';
        const data = await apiClient.request(`/api/ava-video/moment-status/${sessionId}/${momentType}${q}`);
        if (cancelled) return;
        errorCount = 0;
        if (data.status === 'ready' && data.video_url) {
          setVideoUrl(`${API_URL}/api/files/${data.video_url}`);
          stop();
        } else if (data.status === 'failed') {
          stop();
        } else if (data.status === 'not_found') {
          // Trigger may land a beat after the first poll — allow a few retries.
          notFoundCount += 1;
          if (notFoundCount >= 4) stop();
        }
        if (attempts >= MAX_ATTEMPTS) stop();
      } catch (e) {
        if (cancelled) return;
        // Transient failure (429 / network) — keep the interval alive and
        // retry; only give up after several consecutive errors.
        errorCount += 1;
        if (errorCount >= MAX_CONSECUTIVE_ERRORS || attempts >= MAX_ATTEMPTS) stop();
      }
    };

    check();
    intervalId = setInterval(check, 5000);
    return () => { cancelled = true; stop(); };
  }, [sessionId, momentType, enabled, dismissed, avaId]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    setVideoUrl(null);
  }, []);

  return { videoUrl: dismissed ? null : videoUrl, dismiss };
};

export default useMomentVideo;
